import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { ep } from '../api/endpoints';

const GET_ACCOUNTS_URL = ep('get_obc_accounts');
const GET_DEPARTMENTS_URL = ep('get_obc_departments');
const GET_SUB_ACCOUNTS_URL = ep('get_obc_sub_accounts');

export type ObcMasterTab = 'account' | 'department' | 'sub_account';

export interface ObcAccountRow {
  account_code: string;
  account_name: string;
  account_kana: string | null;
  category_l1_code: string | null;
  category_l1_name: string | null;
  category_l2_code: string | null;
  category_l2_name: string | null;
  category_l3_code: string | null;
  category_l3_name: string | null;
  debit_credit_type: number | null;
  is_active: boolean | null;
  synced_at: string | null;
}

export interface ObcDepartmentRow {
  section_code: string;
  section_name: string;
  section_kana: string | null;
  synced_at: string | null;
}

export interface ObcSubAccountRow {
  account_code: string;
  account_name: string | null;
  sub_account_code: string;
  sub_account_name: string | null;
  synced_at: string | null;
}

export const useObcMasterTable = (tab: ObcMasterTab) => {
  const [data, setData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [searchWord, setSearchWord] = useState('');
  const lastRequestId = useRef(0);

  const fetchData = useCallback(async () => {
    const requestId = ++lastRequestId.current;
    setLoading(true);
    setError(null);

    const url =
      tab === 'account'
        ? GET_ACCOUNTS_URL
        : tab === 'department'
        ? GET_DEPARTMENTS_URL
        : GET_SUB_ACCOUNTS_URL;

    try {
      const response = await axios.post(url, {
        page,
        pageSize,
        searchWord,
      });

      if (requestId !== lastRequestId.current) return;

      if (response.data.status === 'success') {
        setData(response.data.rowsResult || []);
        setTotalCount(Number(response.data.total || 0));
        setLastSyncedAt(response.data.lastSyncedAt ?? null);
      } else {
        setError(response.data.message || 'データ取得に失敗しました');
        setData([]);
        setTotalCount(0);
      }
    } catch (err: any) {
      if (requestId !== lastRequestId.current) return;
      console.error('OBC master fetch error:', err);
      setError(err.response?.data?.message || err.message || 'データ取得に失敗しました');
      setData([]);
      setTotalCount(0);
    } finally {
      if (requestId === lastRequestId.current) {
        setLoading(false);
      }
    }
  }, [tab, page, pageSize, searchWord]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const applySearch = useCallback((word: string) => {
    setSearchWord(word);
    setPage(0);
  }, []);

  return {
    data,
    totalCount,
    lastSyncedAt,
    loading,
    error,
    page,
    pageSize,
    setPage,
    setPageSize,
    applySearch,
    refresh: fetchData,
  };
};
